import React, { useState } from 'react';
import { Card, Tag, Button, Modal, message, Avatar, Dropdown } from 'antd';
import {
  CarOutlined,
  EditOutlined,
  ToolOutlined,
  DeleteOutlined,
  MoreOutlined,
  CalendarOutlined,
  DashboardOutlined
} from '@ant-design/icons';
import { Vehicle } from '../types';
import { vehicleApi } from '../services/api';
import { assetLabel } from '../utils/assetLabel';
import VehicleForm from './VehicleForm';


interface VehicleCardProps {
  vehicle: Vehicle;
  onUpdate: () => void;
}

const VehicleCard: React.FC<VehicleCardProps> = ({ vehicle, onUpdate }) => {
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const isEquipment = vehicle.asset_type === 'equipment';

  const formatMileage = (mileage?: number) => {
    return (mileage ?? 0).toLocaleString() + ' miles';
  };

  const getVehicleColor = (make?: string) => {
    const colors = {
      'Toyota': '#FF4444',
      'Honda': '#4169E1',
      'Ford': '#1E90FF',
      'Chevrolet': '#FFD700',
      'BMW': '#000080',
      'Mercedes': '#C0C0C0',
    } as const;
    return colors[make as keyof typeof colors] || '#2563eb';
  };

  const handleEdit = () => {
    setEditModalVisible(true);
  };

  const handleService = () => {
    message.info('Service functionality coming soon!');
  };

  const handleDelete = () => {
    Modal.confirm({
      title: 'Delete Vehicle',
      content: `Are you sure you want to delete this ${assetLabel(vehicle)}?`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          setDeleteLoading(true);
          await vehicleApi.delete(vehicle.id!);
          message.success('Vehicle deleted successfully');
          onUpdate();
        } catch (error) {
          message.error('Failed to delete vehicle');
        } finally {
          setDeleteLoading(false);
        }
      },
    });
  };

  const handleEditSuccess = () => {
    setEditModalVisible(false);
    onUpdate();
  };

  const actionMenuItems = [
    {
      key: 'edit',
      label: 'Edit Vehicle',
      icon: <EditOutlined />,
      onClick: handleEdit,
    },
    {
      key: 'service',
      label: 'Schedule Service',
      icon: <ToolOutlined />,
      onClick: handleService,
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'delete',
      label: 'Delete Vehicle',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: handleDelete,
    },
  ];

  return (
    <>
      <Card
        styles={{ body: { padding: '28px' } }}
        style={{
          height: '100%',
          width: '100%',
          borderRadius: 20,
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          border: '1px solid #e2e8f0',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          transition: 'all 0.3s ease'
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '20px',
          minWidth: 0
        }}>
          {/* Vehicle Icon */}
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <Avatar
              size={64}
              style={{
                background: `linear-gradient(135deg, ${getVehicleColor(vehicle.make)} 0%, ${getVehicleColor(vehicle.make)}dd 100%)`,
                color: 'white',
                border: '3px solid white',
                boxShadow: `0 8px 16px ${getVehicleColor(vehicle.make)}30`
              }}
              icon={<CarOutlined style={{ fontSize: '20px' }} />}
            />
            <div style={{
              position: 'absolute',
              bottom: '-4px',
              right: '-4px',
              width: '20px',
              height: '20px',
              backgroundColor: '#10b981',
              border: '2px solid white',
              borderRadius: '50%'
            }}></div>
          </div>

          {/* Content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              marginBottom: '16px'
            }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: 'bold',
                  color: '#111827',
                  marginBottom: '8px',
                  lineHeight: '1.25',
                  wordBreak: 'break-word'
                }}>
                  {assetLabel(vehicle)}
                </h3>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '8px'
                }}>
                  <Tag
                    color="blue"
                    style={{
                      background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                      color: 'white',
                      boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)'
                    }}
                  >
                    {vehicle.license_plate}
                  </Tag>
                </div>
              </div>
              <Dropdown
                menu={{ items: actionMenuItems }}
                trigger={['click']}
                placement="bottomRight"
              >
                <Button
                  type="text"
                  icon={<MoreOutlined />}
                  style={{ color: '#6b7280' }}
                />
              </Dropdown>
            </div>

            {/* Vehicle Info */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: window.innerWidth >= 640 ? 'repeat(2, 1fr)' : '1fr',
              gap: '16px',
              marginBottom: '24px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px',
                backgroundColor: '#eff6ff',
                borderRadius: '8px',
                border: '1px solid #dbeafe'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  backgroundColor: '#3b82f6',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <DashboardOutlined style={{ color: 'white', fontSize: '14px' }} />
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#2563eb', fontWeight: '500' }}>{isEquipment ? 'Hours' : 'Current Mileage'}</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                    {isEquipment ? `${(vehicle.hours ?? 0).toLocaleString()} hrs` : formatMileage(vehicle.current_mileage)}
                  </div>
                </div>
              </div>

              {Boolean(vehicle.last_service_mileage) && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px',
                  backgroundColor: '#f0fdf4',
                  borderRadius: '8px',
                  border: '1px solid #bbf7d0'
                }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    backgroundColor: '#10b981',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <CalendarOutlined style={{ color: 'white', fontSize: '14px' }} />
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#059669', fontWeight: '500' }}>Last Service</div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>{formatMileage(vehicle.last_service_mileage)}</div>
                  </div>
                </div>
              )}

              {vehicle.color && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px',
                  backgroundColor: '#faf5ff',
                  borderRadius: '8px',
                  border: '1px solid #e9d5ff'
                }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    backgroundColor: '#8b5cf6',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      borderRadius: '50%',
                      backgroundColor: 'white'
                    }}></div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#7c3aed', fontWeight: '500' }}>Color</div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827', textTransform: 'capitalize' }}>{vehicle.color}</div>
                  </div>
                </div>
              )}

              {vehicle.engine_size && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px',
                  backgroundColor: '#fffbeb',
                  borderRadius: '8px',
                  border: '1px solid #fde68a'
                }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    backgroundColor: '#f59e0b',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <span style={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>E</span>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#d97706', fontWeight: '500' }}>Engine</div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>{vehicle.engine_size}</div>
                  </div>
                </div>
              )}
            </div>

            {/* VIN */}
            <div style={{
              background: 'linear-gradient(to right, #f9fafb 0%, #f3f4f6 100%)',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '24px',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '8px'
              }}>
                <div style={{
                  width: '24px',
                  height: '24px',
                  backgroundColor: '#6b7280',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <span style={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>#</span>
                </div>
                <div style={{
                  fontSize: '12px',
                  color: '#4b5563',
                  textTransform: 'uppercase',
                  fontWeight: '600',
                  letterSpacing: '0.05em'
                }}>{isEquipment ? 'Serial Number' : 'VIN Number'}</div>
              </div>
              <div style={{
                fontSize: '14px',
                fontFamily: 'monospace',
                color: '#111827',
                wordBreak: 'break-all',
                lineHeight: '1.625',
                backgroundColor: 'white',
                padding: '8px',
                borderRadius: '8px'
              }}>
                {isEquipment ? (vehicle.serial_number || '—') : vehicle.vin}
              </div>
            </div>

            {/* Notes */}
            {vehicle.notes && (
              <div style={{
                background: 'linear-gradient(to right, #eff6ff 0%, #e0e7ff 100%)',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '24px',
                border: '1px solid #c3ddfd'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '8px'
                }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    backgroundColor: '#3b82f6',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <span style={{ color: 'white', fontSize: '12px' }}>📝</span>
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: '#1d4ed8',
                    textTransform: 'uppercase',
                    fontWeight: '600',
                    letterSpacing: '0.05em'
                  }}>Notes</div>
                </div>
                <div style={{
                  fontSize: '14px',
                  color: '#1e3a8a',
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  lineHeight: '1.625'
                }}>
                  {vehicle.notes}
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingTop: '16px',
              borderTop: '1px solid #f3f4f6',
              flexWrap: 'wrap',
              gap: '12px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                flexWrap: 'wrap'
              }}>
                <Button
                  type="primary"
                  icon={<ToolOutlined />}
                  onClick={handleService}
                  style={{
                    borderRadius: '8px',
                    fontWeight: '500',
                    background: 'linear-gradient(to right, #3b82f6 0%, #2563eb 100%)',
                    border: 'none',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {window.innerWidth >= 768 ? 'Schedule Service' : 'Service'}
                </Button>
                <Button
                  icon={<EditOutlined />}
                  onClick={handleEdit}
                  style={{
                    borderRadius: '8px',
                    fontWeight: '500',
                    color: '#4b5563',
                    backgroundColor: 'transparent',
                    border: '1px solid #e5e7eb',
                    transition: 'all 0.3s ease'
                  }}
                >
                  Edit
                </Button>
              </div>

              <Button
                icon={<DeleteOutlined />}
                onClick={handleDelete}
                loading={deleteLoading}
                danger
                style={{
                  borderRadius: '8px',
                  fontWeight: '500',
                  backgroundColor: 'transparent',
                  border: '1px solid #fca5a5',
                  transition: 'all 0.3s ease'
                }}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <Modal
        title={`Edit ${assetLabel(vehicle)}`}
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={null}
        width="95%"
        style={{ maxWidth: 800, top: window.innerWidth >= 768 ? 20 : 10 }}
        styles={{ body: { maxHeight: 'calc(100vh - 120px)', overflowY: 'auto' } }}
        destroyOnHidden
      >
        <VehicleForm
          customerId={vehicle.customer_id}
          propVehicleId={vehicle.id}
          onSuccess={handleEditSuccess}
          onCancel={() => setEditModalVisible(false)}
          modal
        />
      </Modal>
    </>
  );
};

export default VehicleCard;